import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { ClipboardCheckIcon, MessageSquareTextIcon } from 'lucide-react'
import type { CoachingGradingMode } from '@/hooks/coaching/usePersistedGradingMode'

interface Props {
  mode: CoachingGradingMode
  onChange: (mode: CoachingGradingMode) => void
  disabled?: boolean
}

const OPTIONS: ReadonlyArray<{ value: CoachingGradingMode; Icon: typeof ClipboardCheckIcon }> = [
  { value: 'grading', Icon: ClipboardCheckIcon },
  { value: 'coaching', Icon: MessageSquareTextIcon },
]

/**
 * [채점 / 코칭] 세그먼트 스위치 — 레슨·리뷰노트·테스트·단원평가 채점 화면이 공유한다.
 * 진도/복습 회차 스위치와 같은 시각 언어(테두리 트랙 + 선택 항목만 흰 배경·그림자)를 써서
 * 단순 텍스트가 아니라 전환 컨트롤임이 드러나게 한다.
 */
export function GradingModeSwitch({ mode, onChange, disabled = false }: Props) {
  const { t } = useTranslation('coaching')
  return (
    <div
      role="group"
      aria-label={t('gradingMode.switchLabel')}
      data-testid="grading-mode-switch"
      className="inline-flex shrink-0 items-center rounded-md border border-border bg-muted p-0.5"
    >
      {OPTIONS.map(({ value, Icon }) => {
        const active = mode === value
        return (
          <button
            key={value}
            type="button"
            aria-pressed={active}
            disabled={disabled}
            onClick={() => onChange(value)}
            className={clsx(
              'inline-flex h-7 items-center gap-1.5 rounded-[5px] px-3 text-xs font-semibold transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
              'disabled:cursor-not-allowed disabled:opacity-50',
              active
                ? 'bg-background text-foreground shadow-sm ring-1 ring-border'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <Icon aria-hidden="true" className="size-3.5" />
            {t(`gradingMode.${value}`)}
          </button>
        )
      })}
    </div>
  )
}
